// Accept / Reject a PENDING quote. Accept is the terminal action for the item's
// quoting phase: the backend atomically accepts this quote, auto-rejects every
// other PENDING quote on the item, and flips the item to FULFILLED. The reason
// is optional on both paths — but accept must always SEND the reason key: its
// DTO is @IsString without @IsNotEmpty, so '' passes while an absent field 400s
// (reject's DTO is @IsOptional, so omitting is fine there).

import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@elkaso-app/web-design';
import {
  RestaurantInquiryQuote,
  useAcceptInquiryItemQuoteApi,
  useRejectInquiryItemQuoteApi,
} from 'apis/restaurant-inquiries';
import { apiErrorMessage } from 'pages/inquiry-processor/utils/inquiry-actions';
import { useEffect, useState } from 'react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  inquiryId: string;
  itemId: number;
  quote: RestaurantInquiryQuote;
  mode: 'accept' | 'reject';
}

export const QuoteDecisionModal = ({ isOpen, onClose, inquiryId, itemId, quote, mode }: Props) => {
  const toast = useToast();
  const acceptQuote = useAcceptInquiryItemQuoteApi();
  const rejectQuote = useRejectInquiryItemQuoteApi();
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (isOpen) setReason('');
  }, [isOpen]);

  const isAccept = mode === 'accept';
  const isLoading = acceptQuote.isLoading || rejectQuote.isLoading;
  const supplierName = quote.supplier?.name || `Supplier #${quote.supplierId}`;

  const confirm = async () => {
    try {
      if (isAccept) {
        await acceptQuote.mutateAsync({ id: inquiryId, itemId, quoteId: quote.id, reason: reason.trim() });
      } else {
        await rejectQuote.mutateAsync({ id: inquiryId, itemId, quoteId: quote.id, reason: reason.trim() || undefined });
      }
    } catch (err) {
      toast({
        title: `Failed to ${mode} quote`,
        description: apiErrorMessage(err),
        status: 'error',
      });
      return;
    }
    toast({ title: isAccept ? 'Quote accepted — item FULFILLED' : 'Quote rejected', status: 'success' });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: 'md' }}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{isAccept ? 'Accept Quote' : 'Reject Quote'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize='sm' mb='3'>
            {supplierName} — {quote.product?.nameEn || `SKU #${quote.productId}`} at{' '}
            <b>
              {quote.price} {quote.currency}
            </b>
          </Text>
          {isAccept && (
            <Text fontSize='sm' color='orange.600' mb='3'>
              Accepting is final: every other PENDING quote on this item is rejected and the item becomes FULFILLED.
            </Text>
          )}
          <FormControl>
            <FormLabel fontSize='sm'>
              Reason{' '}
              <Text as='span' fontWeight='400' color='gray.500'>
                (optional)
              </Text>
            </FormLabel>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={isAccept ? 'e.g. Best price and fastest delivery' : 'e.g. Price too high'}
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && confirm()}
            />
          </FormControl>
        </ModalBody>
        <ModalFooter gap='2'>
          <Button variant='outline' onClick={onClose} isDisabled={isLoading}>
            Cancel
          </Button>
          <Button
            colorScheme={isAccept ? 'green' : 'red'}
            onClick={confirm}
            isLoading={isLoading}
            loadingText={isAccept ? 'Accepting…' : 'Rejecting…'}>
            {isAccept ? 'Accept Quote' : 'Reject Quote'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
